/**
 * @file MenuItemRow.tsx
 * @description Строка пункта меню (phase19.16 Р1; SB16, SB81; uiux-design-r1
 * §3): ручка перетаскивания 48×48, подпись, вид и цель, пометки «скрыт:
 * страница не опубликована» / «скрыт с сайта», меню «⋯» (Изменить, Выше,
 * Ниже, Вложить / Вынести, Скрыть / Показать, Удалить — запасной путь без
 * перетаскивания для касания и клавиатуры).
 *
 * Партия «Юридические документы»: у цели-группы вид «Группа документов» —
 * по общему списку документов (`useLegalTargetList`, один запрос на все
 * строки; грузится только у строк с документом).
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";

import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import { useLegalTargetList } from "../../../hooks/site-view/useLegalTargetList";
import { menuTypeShort } from "../../../lib/site-view/menu-link-types";
import type { SiteMenuItem } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

/** Пометка о цели пункта (SB16) — реестром по статусу. */
const TARGET_NOTE: Readonly<Record<SiteMenuItem["target"]["status"], string | null>> = {
  ok: null,
  none: null,
  unpublished: "Страница не опубликована — пункт скрыт на сайте, пока её не опубликуют.",
  deleted: "Цель удалена — пункт не показывается. Выберите другую или удалите пункт.",
};

interface Props {
  item: SiteMenuItem;
  nested: boolean;
  actions: RowAction[];
  /** Пометка под строкой (подвал: «ссылка уже есть внизу подвала», SB115). */
  notice?: ReactNode;
}

export function MenuItemRow({ item, nested, actions, notice }: Props) {
  const sortable = useSortable({ id: item.id });
  const isLegal = item.linkType === "LEGAL_DOCUMENT";
  const legal = useLegalTargetList({ enabled: isLegal });
  const legalKind = isLegal ? (legal.targets?.find((t) => t.id === item.legalDocumentId)?.kind ?? null) : null;
  const where = item.target.href ?? (item.linkType === "GROUP" ? `${item.children.length} в колонке` : null);
  const note = TARGET_NOTE[item.target.status];

  return (
    <div
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      className={cn(
        "flex flex-col bg-surface-lowest",
        nested && "pl-6",
        sortable.isDragging && "relative z-10 rounded-lg shadow-lg",
      )}
    >
      <div className="flex min-h-14 items-center gap-1">
        <button
          type="button"
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`Перетащить «${item.label ?? item.target.title}»`}
          className="inline-flex size-12 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <div className="flex min-w-0 flex-1 flex-col py-1">
          <span title={item.label ?? item.target.title} className={cn("type-body truncate font-medium", item.isHidden ? "text-on-surface-variant" : "text-on-surface")}>
            {item.label ?? item.target.title}
          </span>
          <span className="type-caption truncate text-on-surface-variant">
            {menuTypeShort(item.linkType, legalKind)}
            {where ? ` · ${where}` : ""}
          </span>
        </div>
        {item.isHidden ? <StatusPill tone="neutral">Скрыт</StatusPill> : null}
        <RowActionsMenu actions={actions} title={`Пункт «${item.label ?? item.target.title}»`} size="compact" />
      </div>
      {note ? <p className="type-caption pb-2 pl-12 text-warning">! {note}</p> : null}
      {notice ? <div className="type-caption pb-2 pl-12 pr-3 text-on-surface-variant">{notice}</div> : null}
      {item.issues.map((issue) => (
        <p key={`${issue.code}:${issue.path}`} className="type-caption pb-2 pl-12 text-destructive">
          {issue.message}
        </p>
      ))}
    </div>
  );
}
